import { resultsForTab, searchTabs, type SearchResults, type SearchTab } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/**
 * La ricerca sul telefono: **stesso dominio della TV, altro ingresso**.
 *
 * ## Cosa cambia davvero, e cosa no
 *
 * Non cambia niente di cosa si cerca e di come si ordina: `searchCatalog`, `searchTabs` e
 * `resultsForTab` sono gli stessi del televisore, letteralmente. Cambia **come si scrive**: là una
 * tastiera a schermo guidata dalle frecce, qui un campo di testo vero con la tastiera del sistema
 * (`04-SCHERMATE-MOBILE.md` §6).
 *
 * È la dimostrazione del motivo per cui la logica sta nel dominio: due grammatiche d'ingresso, una
 * sola definizione di «cosa vuol dire cercare».
 *
 * ## Le ricerche recenti si vedono quando servono
 *
 * Compaiono con il campo vuoto, cioè **prima** di scrivere, perché è lì che risparmiano la digitazione.
 * Lasciandole sotto i risultati non le vedrebbe nessuno: su 390 px sarebbero oltre il bordo.
 */
export function PhoneSearchScreen({
  query,
  onQueryChange,
  results,
  activeTab,
  onPickTab,
  recent,
  onPickRecent,
  onOpen,
}: {
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly results: SearchResults;
  readonly activeTab: SearchTab;
  readonly onPickTab: (tab: SearchTab) => void;
  readonly recent: ReadonlyArray<string>;
  readonly onPickRecent: (query: string) => void;
  /** Cosa aprire: il tipo serve perché lo stesso identificativo può esistere in due cataloghi. */
  readonly onOpen: (kind: 'tv' | 'movie' | 'series' | 'channel', id: string) => void;
}) {
  const translator = useTranslator();
  const tabs = searchTabs(results);
  const visibili = resultsForTab(results, activeTab);
  const vuoto = query.trim() === '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '8px 0 28px' }}>
      <h1 style={{ margin: 0, padding: '0 20px', font: `800 28px ${FONT_STACK}`, letterSpacing: '-.02em' }}>
        {translator.t('menu.search')}
      </h1>

      <div style={{ padding: '0 20px' }}>
        <input
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder={translator.t('phone.search.placeholder')}
          aria-label={translator.t('phone.search.placeholder')}
          /*
            `autoFocus` **non** c'è, ed è deliberato: aprirebbe la tastiera di sistema appena si tocca
            la scheda, coprendo metà schermo — comprese le ricerche recenti, che sono lì proprio per
            far scrivere di meno.
          */
          style={{
            width: '100%',
            height: 44,
            boxSizing: 'border-box',
            padding: '0 14px',
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            background: colors.surface,
            color: colors.text,
            font: `500 15px ${FONT_STACK}`,
          }}
        />
      </div>

      {vuoto && recent.length > 0 && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h2 style={{ margin: 0, padding: '0 20px', font: `700 13px ${FONT_STACK}`, color: colors.textDim }}>
            {translator.t('phone.search.recent')}
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 20px' }}>
            {recent.map((voce) => (
              <button
                key={voce}
                type="button"
                onClick={() => onPickRecent(voce)}
                style={{
                  height: 36,
                  padding: '0 14px',
                  border: 0,
                  borderRadius: 18,
                  background: colors.surfaceButton,
                  color: colors.text,
                  font: `600 14px ${FONT_STACK}`,
                  cursor: 'pointer',
                }}
              >
                {voce}
              </button>
            ))}
          </div>
        </section>
      )}

      {!vuoto && tabs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none', padding: '0 20px' }}>
          {tabs.map((scheda) => (
            <button
              key={scheda.tab}
              type="button"
              onClick={() => onPickTab(scheda.tab)}
              aria-pressed={scheda.tab === activeTab}
              style={{
                flex: 'none',
                height: 36,
                padding: '0 14px',
                border: 0,
                borderRadius: 18,
                background: scheda.tab === activeTab ? colors.accent : colors.surfaceButton,
                color: scheda.tab === activeTab ? colors.background : colors.text,
                font: `600 14px ${FONT_STACK}`,
                cursor: 'pointer',
              }}
            >
              {/*
                Il conteggio accanto al nome: dice se vale la pena cambiare scheda **prima** di
                toccarla. Le schede vuote non ci sono affatto — lo decide `searchTabs`.
              */}
              {translator.t(`search.tab.${scheda.tab}`)} · {scheda.count}
            </button>
          ))}
        </div>
      )}

      {!vuoto && visibili.total === 0 && (
        // Si nomina cosa si è cercato: su una tastiera di telefono il refuso è più probabile di un
        // catalogo che davvero non ha quel titolo.
        <p style={{ margin: 0, padding: '20px', font: `500 14px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('phone.search.none', { query: query.trim() })}
        </p>
      )}

      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column' }}>
        {visibili.channels.map((channel) => (
          <Row key={`c-${channel.id}`} label={channel.name} onPick={() => onOpen('channel', channel.id)} />
        ))}
        {visibili.tv.map((program) => (
          <Row key={`t-${program.id}`} label={program.title} onPick={() => onOpen('tv', program.id)} />
        ))}
        {visibili.movies.map((item) => (
          <Row key={`m-${item.id}`} label={item.title} onPick={() => onOpen('movie', item.id)} />
        ))}
        {visibili.series.map((item) => (
          <Row key={`s-${item.id}`} label={item.title} onPick={() => onOpen('series', item.id)} />
        ))}
      </ul>
    </div>
  );
}

/** Una riga di risultato: alta 52 px perché è un bersaglio per un dito, non per un puntatore. */
function Row({ label, onPick }: { readonly label: string; readonly onPick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        style={{
          width: '100%',
          minHeight: 52,
          border: 0,
          borderBottom: `1px solid ${colors.borderFaint}`,
          background: 'none',
          color: colors.text,
          textAlign: 'left',
          cursor: 'pointer',
          padding: '0 20px',
          font: `600 15px ${FONT_STACK}`,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </button>
    </li>
  );
}
